import { useSelectionInfoContext } from '../contexts/SelectionInfoContext';
import { useRowOverrideContext } from '../pages/SharedMenuEditorPage/components/OfferDetailsForm/Fields/WithOverrideToggle';
import { BaseField } from '../pages/SharedMenuEditorPage/components/OfferDetailsForm/Fields/types';

export type FieldState = 'previewing' | 'previewing-overridden' | 'previewing-locked' | 'editing';

type UseFieldStateProps = {
  sidePanelMode: BaseField['sidePanelMode'];
  isOverridden: boolean;
  canBeOverridden: boolean;
  hasContentEditPermission: boolean;
  isOnlyForVenueLevel: boolean;
};

export const useFieldState = ({
  sidePanelMode,
  isOverridden,
  canBeOverridden,
  hasContentEditPermission,
  isOnlyForVenueLevel,
}: UseFieldStateProps): FieldState => {
  const { vlmeConfig } = useSelectionInfoContext();
  const { isOverrideToggleOn } = useRowOverrideContext();

  const isVlme = !!vlmeConfig;
  const isSharedVenue = isVlme && vlmeConfig.isSharedVenue;
  const isSingleVenue = isVlme && vlmeConfig.isSingleVenue;
  const isSharedMenu = !isVlme;

  return getFieldState({
    isSharedVenue,
    isSingleVenue,
    isSharedMenu,
    sidePanelMode,
    canBeOverridden,
    isOverrideToggleOn,
    isOverridden,
    hasContentEditPermission,
    isOnlyForVenueLevel,
  });
};

export type GetFieldStateArgs = {
  isSharedVenue: boolean;
  isSingleVenue: boolean;
  isSharedMenu: boolean;
  sidePanelMode: UseFieldStateProps['sidePanelMode'];
  hasContentEditPermission: boolean;
  isOnlyForVenueLevel: boolean;
  canBeOverridden?: boolean;
  isOverrideToggleOn?: boolean;
  isOverridden?: boolean;
};

export const getFieldState = ({
  isSharedVenue,
  isSingleVenue,
  isSharedMenu,
  sidePanelMode,
  hasContentEditPermission,
  isOnlyForVenueLevel,
  canBeOverridden = false,
  isOverrideToggleOn = false,
  isOverridden = false,
}: GetFieldStateArgs): FieldState => {
  if (sidePanelMode === 'preview' && isSharedVenue && isOverridden) {
    return 'previewing-overridden';
  }

  if (sidePanelMode === 'edit' && isOnlyForVenueLevel && !isSharedMenu) {
    return 'editing';
  }

  if (!hasContentEditPermission) {
    return sidePanelMode === 'preview' ? 'previewing' : 'previewing-locked';
  }

  if (isSharedMenu || isSingleVenue) {
    return sidePanelMode === 'create' || sidePanelMode === 'edit' ? 'editing' : 'previewing';
  }

  if (sidePanelMode === 'edit' && isSharedVenue && !canBeOverridden) {
    return 'previewing-locked';
  }

  if ((sidePanelMode === 'edit' || sidePanelMode === 'create') && !isSharedVenue) {
    return 'editing';
  }

  if (sidePanelMode === 'edit' && canBeOverridden && isOverrideToggleOn) {
    return 'editing';
  }

  if (sidePanelMode === 'edit' && canBeOverridden && !isOverrideToggleOn) {
    return 'previewing';
  }

  return 'previewing';
};
